/**
 * Is the server there, and when was it last (plan.md step 54)?
 *
 * The version matrix says both. The connection is OFFLINE until the server
 * answers — the user's rule (2026-10-06): a phone in a field should not be
 * told it is online because an interface is up — ONLINE when it answers
 * promptly, LIMITED when it answers slowly (festival wifi with a thin pipe
 * behind it).
 */
import { readJson, writeJson } from './storage';

export type Connection = 'online' | 'limited' | 'offline';

/** An answer slower than this is a LIMITED connection. */
export const LIMITED_AFTER_MS = 2_500;

/**
 * The server answered (any HTTP status: a 401 came FROM the server). Kept in
 * `myrave:platform:reached`; written at most every 5 s, because the API client
 * calls this on every response.
 */
export function noteServerReached(at = Date.now()): void {
  const previous = lastReached();
  if (previous !== null && at >= previous && at - previous < 5_000) return;
  writeJson(null, 'reached', at);
}

/** When this device last heard from the server, epoch ms — or null, never. */
export function lastReached(): number | null {
  return readJson<number | null>(null, 'reached', null);
}

/** Ask `/health` once: Online, Limited (slow, or up but degraded) or Offline. */
export async function probeConnection(
  apiBase: string,
  { limitedAfterMs = LIMITED_AFTER_MS, timeoutMs = 8_000 }: { limitedAfterMs?: number; timeoutMs?: number } = {},
): Promise<Connection> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const started = Date.now();
  try {
    const res = await Promise.race([
      fetch(`${apiBase}/health`, { cache: 'no-store', signal: controller.signal }),
      new Promise<never>((_, reject) => controller.signal.addEventListener('abort', () => reject(new Error('timeout')))),
    ]);
    noteServerReached();
    if (!res.ok) return 'limited';
    return Date.now() - started > limitedAfterMs ? 'limited' : 'online';
  } catch {
    return 'offline';
  } finally {
    clearTimeout(timer);
  }
}
